import React from 'react';
import { Users, Monitor, Film, Layers } from 'lucide-react';
import { formatCount, layeringHeadline, LayeringStep } from '../lib/audienceProfile';

/**
 * Media-plan scenarios, as cards.
 *
 * Fifteen scenarios went out as fifteen stacked markdown headings with two
 * bullet lists each, in generation order — so comparing any two meant
 * scrolling past the rest, and "which reaches the most people" took the
 * longest to answer. The backend now sends a `json:scenarios` payload ranked
 * by reach and this lays it out in a grid.
 */
export interface ScenarioCard {
  id: string;
  label: string;
  audience: string;
  format: string;
  devices: string[];
  reach: number;
  reach_display: string;
  impressions_display: string;
  views_display: string;
  cpm: number;
  cost: number;
  reach_share: number;
}

export interface AudienceLayering {
  steps: LayeringStep[];
  unpriced?: string[];
}

export interface ScenarioCardsPayload {
  currency?: string;
  count?: number;
  scenarios?: ScenarioCard[];
  layering?: AudienceLayering;
}

export const SCENARIOS_FENCE = /```json:scenarios\n([\s\S]*?)\n```/;

export const parseScenarios = (text: string): ScenarioCardsPayload | null => {
  const match = text.match(SCENARIOS_FENCE);
  if (!match) return null;
  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
};

export const stripScenarios = (text: string): string =>
  text.replace(SCENARIOS_FENCE, '').replace(/\n{3,}/g, '\n\n').trim();

const money = (value: number, currency?: string) => {
  const rendered = value.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  return currency ? `${rendered} ${currency}` : rendered;
};

const Metric: React.FC<{ label: string; value: string; strong?: boolean }> = ({
  label, value, strong,
}) => (
  <div>
    <div style={{
      fontSize: '10px', fontWeight: 600, letterSpacing: '0.05em',
      textTransform: 'uppercase', color: '#9ca3af', marginBottom: '1px',
    }}>
      {label}
    </div>
    <div style={{
      fontSize: strong ? '19px' : '14px',
      fontWeight: strong ? 700 : 600,
      color: strong ? '#6d28d9' : '#111827',
      lineHeight: 1.2,
    }}>
      {value}
    </div>
  </div>
);

const Tag: React.FC<{ tone: 'warn' | 'muted'; children: React.ReactNode }> = ({ tone, children }) => (
  <span style={{
    fontSize: '10px', fontWeight: 600, borderRadius: '4px', padding: '1px 6px', whiteSpace: 'nowrap',
    background: tone === 'warn' ? '#fef3c7' : '#f3f4f6',
    color: tone === 'warn' ? '#92400e' : '#6b7280',
  }}>
    {children}
  </span>
);

/**
 * What each approved audience adds, biggest first.
 *
 * Every scenario forecasts one audience, so the cards cannot answer the
 * budget question of a multi-audience plan: how many new people does the
 * second audience bring? The ladder can. Its figures are audience sizes from
 * Audience Insights, not campaign reach, and the caption says so, because the
 * two sit side by side here and are not comparable.
 */
const LayeringPanel: React.FC<{ layering: AudienceLayering }> = ({ layering }) => {
  const steps = layering.steps || [];
  const total = steps.length ? steps[steps.length - 1].cumulative : 0;
  if (steps.length < 2 || !(total > 0)) return null;
  const headline = layeringHeadline(steps);

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px',
      padding: '14px 16px', marginBottom: '16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Layers size={15} style={{ color: '#8b5cf6' }} />
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700 }}>What each audience adds</h4>
      </div>
      {headline && (
        <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#374151' }}>{headline}</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {steps.map((st, i) => {
          const before = st.cumulative - st.added;
          return (
            <div key={st.name + i}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap',
                fontSize: '12px', marginBottom: '3px',
              }}>
                <span style={{ fontWeight: 600, color: '#111827' }}>{st.name}</span>
                <span style={{ color: '#6b7280' }}>
                  {i === 0
                    ? `${formatCount(st.added)} people`
                    : `+${formatCount(st.added) || '0'} new of ${formatCount(st.reach)} (${Math.round(st.added_share * 100)}%)`}
                </span>
                {st.adds_little && <Tag tone="warn">Mostly the same people</Tag>}
                {st.uncertain && <Tag tone="muted">Too small to measure here</Tag>}
                {st.in_scenarios === false && <Tag tone="muted">No scenario</Tag>}
              </div>
              {/* Grey is everyone the audiences above already reach; purple
                  is who this one adds. All bars share the total's scale. */}
              <div style={{
                display: 'flex', height: '6px', background: '#f3f4f6',
                borderRadius: '3px', overflow: 'hidden',
              }}>
                <div style={{ width: `${(before / total) * 100}%`, background: '#e5e7eb' }} />
                <div style={{ width: `${(st.added / total) * 100}%`, background: '#8b5cf6' }} />
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#9ca3af' }}>
        Potential YouTube audience from Google Audience Insights, counted once per person.
        These are audience sizes, not the forecast reach of a campaign.
        {layering.unpriced && layering.unpriced.length > 0 &&
          ` Not sized: ${layering.unpriced.join(', ')}.`}
      </p>
    </div>
  );
};

const ScenarioCards: React.FC<{ payload: ScenarioCardsPayload }> = ({ payload }) => {
  const scenarios = payload.scenarios || [];
  if (scenarios.length === 0) return null;
  const currency = payload.currency;

  return (
    <div style={{ marginTop: '8px' }}>
      {payload.layering && <LayeringPanel layering={payload.layering} />}

      <div style={{
        display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px',
      }}>
        <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>
          {scenarios.length} scenario{scenarios.length === 1 ? '' : 's'}
        </h3>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>ranked by forecast reach</span>
      </div>

      {/* auto-fit puts two or more side by side whenever the panel allows,
          and drops to one on a narrow screen without a breakpoint. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '12px',
      }}>
        {scenarios.map((s, i) => (
          <div
            key={s.id + i}
            style={{
              background: '#fff',
              border: i === 0 ? '1px solid #c4b5fd' : '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '14px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '11px', fontWeight: 700, color: '#6b7280',
                background: '#f3f4f6', borderRadius: '5px', padding: '2px 7px',
              }}>
                #{i + 1}
              </span>
              <span style={{
                fontSize: '13px', fontWeight: 700, color: '#111827',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {s.label}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#4b5563' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Users size={13} style={{ flexShrink: 0, color: '#9ca3af' }} />
                {s.audience}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Film size={13} style={{ flexShrink: 0, color: '#9ca3af' }} />
                {s.format}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Monitor size={13} style={{ flexShrink: 0, color: '#9ca3af' }} />
                {s.devices.join(', ') || '—'}
              </span>
            </div>

            {/* One bar per card, all on the same scale, so the ranking is
                visible without reading every number. */}
            <div style={{ height: '4px', background: '#f3f4f6', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.max(2, Math.round((s.reach_share || 0) * 100))}%`,
                height: '100%',
                background: i === 0 ? '#8b5cf6' : '#c4b5fd',
              }} />
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
              gap: '10px',
            }}>
              <Metric label="Reach" value={s.reach_display} strong />
              <Metric label="Impressions" value={s.impressions_display} />
              <Metric label="Views" value={s.views_display} />
              <Metric label="CPM" value={money(s.cpm, currency)} />
              <Metric label="Cost" value={money(s.cost, currency)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScenarioCards;
